import React from 'react';
import PropTypes from 'prop-types';

import { P } from '@decisiv/ui-components/lib/components/Typography';

import PatchedTable from 'components/PatchedTable';
import LoadingState from 'components/pages/Users/components/LoadingState';
import PaginationFooter from 'components/pages/Users/components/PaginationFooter';
import { ASCENDING, DESCENDING } from 'components/pages/Users/utils/sortUtils';

import AccountsCell from './AccountsCell';
import AuthenticationCell, { AUTHENTICATION } from './AuthenticationCell';
import PermissionsCell from './PermissionsCell';
import RowActionsMenu from './RowActionsMenu';
import UserCell from './UserCell';
import UsersEmptyState from './UsersEmptyState';
import UsersNoMatches from './UsersNoMatches';
import { REMOVED_COLUMN } from './accessCopy';
import { RowActionsCell, StateContainer } from './styles';
import EMPTY_VALUE from '../../shared/emptyValue';
import { PERMISSION_ASSIGNMENT } from '../../shared/roleTag';

/**
 * Users list for the User Management module (PD-4063).
 *
 * The TEAMS column in the mockup is deliberately absent: teams do not exist
 * until PD-4213. So is AUTHENTICATION, which is not this ticket's.
 *
 * On the Removed Access tab (PD-4677) the ACCOUNTS and PERMISSIONS cells hold
 * the access the user used to have, a REMOVED column says when they lost it,
 * and the row's action restores rather than removes.
 *
 * PatchedTable rather than the KeyDS Table directly — it carries portal's
 * stacking-context and Firefox-in-modal fixes.
 */
function UsersTable({
  canEdit,
  error,
  isLoading,
  isNarrowed,
  isRemoved,
  onPageChange,
  onRemoveAccess,
  onRestoreAccess,
  onSort,
  pagination,
  query,
  sortBy,
  sortOrder,
  users,
}) {
  const columns = [
    {
      name: 'fullname',
      title: 'Users',
      sortBy: onSort('fullname'),
      DataCell: ({ rowData }) => (
        <UserCell email={rowData.email} id={rowData.id} name={rowData.name} />
      ),
    },
    {
      name: 'accounts',
      title: 'Accounts',
      DataCell: ({ rowData }) => <AccountsCell accounts={rowData.accounts} />,
    },
    {
      name: 'permissions',
      title: 'Permissions',
      DataCell: ({ rowData }) => (
        <PermissionsCell
          permissionAssignments={rowData.permissionAssignments}
          permissions={rowData.permissions}
        />
      ),
    },
    {
      name: 'job_role',
      title: 'Job Role',
      sortBy: onSort('job_role'),
      DataCell: ({ rowData }) => <P>{rowData.jobRole || EMPTY_VALUE}</P>,
    },
    {
      name: 'last_login_at',
      title: 'Last Login',
      sortBy: onSort('last_login_at'),
      DataCell: ({ rowData }) => <P>{rowData.lastLoginAt || EMPTY_VALUE}</P>,
    },
    // PD-4684. Not sortable: the value is composed from a domain rule and a
    // separate assignments table rather than read off a column, so
    // ManagementUsersQuery has nothing to ORDER BY.
    {
      name: 'authentication',
      title: 'Authentication',
      DataCell: ({ rowData }) => (
        <AuthenticationCell authentication={rowData.authentication} />
      ),
    },
  ];

  // Requirement 4 on the ticket, which the mockup's Removed Access tab does not
  // draw — its header is identical to the Current one. Added because the ticket
  // asks for it in as many words; raised on the ticket.
  if (isRemoved) {
    columns.push({
      name: 'removed_at',
      title: REMOVED_COLUMN,
      DataCell: ({ rowData }) => <P>{rowData.removedAt || EMPTY_VALUE}</P>,
    });
  }

  // Last, and only for an admin who may act: a menu whose every item is denied
  // is worse than no menu. View Details is reachable from the name either way.
  if (canEdit) {
    columns.push({
      name: 'actions',
      title: 'Actions',
      DataCell: ({ rowData }) => (
        <RowActionsCell>
          <RowActionsMenu
            isRemoved={isRemoved}
            onRemove={onRemoveAccess}
            onRestore={onRestoreAccess}
            user={rowData}
          />
        </RowActionsCell>
      ),
    });
  }

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <StateContainer>
        <P color="danger">Error loading users: {error}</P>
      </StateContainer>
    );
  }

  // Nothing to show at all drops the headers and the footer; nothing *matching*
  // keeps both, so the user can widen or undo what they narrowed. The two states
  // are otherwise easy to conflate — see UsersEmptyState and UsersNoMatches.
  if (!users.length && !isNarrowed) {
    return <UsersEmptyState />;
  }

  const { page, perPage, totalItems, totalPages } = pagination;

  // Restores the header arrow when the sort came from the URL on a reload.
  const defaultSort = sortBy
    ? {
        sortingColumn: sortBy,
        sortingType: sortOrder === 'DESC' ? DESCENDING : ASCENDING,
      }
    : undefined;

  return (
    <>
      <PatchedTable
        columns={columns}
        data={users}
        defaultSort={defaultSort}
        getRowKey={(rowData) => rowData.id}
        footer={() => (
          <PaginationFooter
            page={page}
            pageSize={perPage}
            totalItems={totalItems}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        )}
      />
      {!users.length && <UsersNoMatches query={query} />}
    </>
  );
}

UsersTable.propTypes = {
  /** Whether the viewer may act on a row, which is what draws the ACTIONS column. */
  canEdit: PropTypes.bool,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  /** Whether a search or filter is active, which decides which empty state shows. */
  isNarrowed: PropTypes.bool,
  /** Whether these rows are the Removed Access tab's. */
  isRemoved: PropTypes.bool,
  onPageChange: PropTypes.func.isRequired,
  onRemoveAccess: PropTypes.func,
  onRestoreAccess: PropTypes.func,
  onSort: PropTypes.func.isRequired,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    perPage: PropTypes.number,
    totalItems: PropTypes.number,
    totalPages: PropTypes.number,
  }).isRequired,
  /** The active search term, quoted back in the No Matches title. */
  query: PropTypes.string,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.string,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
      email: PropTypes.string,
      jobRole: PropTypes.string,
      accounts: PropTypes.arrayOf(PropTypes.string),
      permissions: PropTypes.arrayOf(PropTypes.string),
      permissionAssignments: PropTypes.arrayOf(PERMISSION_ASSIGNMENT),
      authentication: AUTHENTICATION,
      lastLoginAt: PropTypes.string,
      removedAt: PropTypes.string,
    }),
  ),
};

UsersTable.defaultProps = {
  canEdit: false,
  error: null,
  isLoading: false,
  isNarrowed: false,
  isRemoved: false,
  onRemoveAccess: () => {},
  onRestoreAccess: () => {},
  query: '',
  sortBy: null,
  sortOrder: null,
  users: [],
};

export default UsersTable;
